<template>
  <section class="gx-mc custom-el-wrapper">
    <gx-loading :show="loading && !loaded" text="加载搬家配置..." />

    <header class="gx-mc__head">
      <button type="button" class="gx-mc__back" @click="requestBack">
        <span class="gx-mc__back-icon">‹</span>
        返回抖店列表
      </button>
      <div class="gx-mc__heading">
        <h1 class="gx-mc__title">{{ isGlobalShop ? '系统预设属性' : '搬家配置' }}</h1>
        <span v-if="activeShopName" class="gx-mc__shop">{{ activeShopName }}</span>
        <span class="gx-mc__plat">抖店</span>
      </div>
      <div class="gx-mc__head-actions">
        <span class="gx-mc__shop-label">当前店铺</span>
        <el-select
          v-model="selectedShopId"
          class="gx-mc__shop-select"
          filterable
          :loading="shopsLoading"
          :disabled="shopsLoading || !shops.length || attributeSaving || templateManageSaving || installationSaving"
          placeholder="请选择抖店"
          no-data-text="暂无抖店"
          @change="onShopSelect"
        >
          <el-option
            v-for="item in shops"
            :key="item.shop_id"
            :label="item.shop_name"
            :value="Number(item.shop_id)"
          >
            <span class="gx-mc__option-row">
              <span class="gx-mc__option-name">{{ item.shop_name }}</span>
              <span class="gx-mc__option-tags">
                <span class="gx-mc__option-meta">{{ item.group_name || '未分组' }}</span>
                <span v-if="Number(item.app_product_status) !== 1" class="gx-mc__option-warn">商品应用异常</span>
              </span>
            </span>
          </el-option>
        </el-select>
        <button type="button" class="gx-mc__refresh" :disabled="loading || syncing || attributeSaving || templateManageSaving || installationSaving" @click="refreshConfig">刷新</button>
      </div>
    </header>

    <div v-if="shopsError" class="gx-mc__inline-error">
      {{ shopsError }}
      <button type="button" @click="loadShopOptions">重试</button>
    </div>

    <nav class="gx-mc__tabs" role="tablist" aria-label="搬家配置分类">
      <button
        v-for="tab in topTabs"
        :key="tab.key"
        type="button"
        class="gx-mc__tab"
        :class="{ 'gx-mc__tab--on': activeTab === tab.key }"
        role="tab"
        :aria-selected="activeTab === tab.key"
        @click="switchTab(tab.key)"
      >
        {{ tab.label }}
        <span v-if="tab.key === 'basic' && installationDirty" class="gx-mc__tab-dot" aria-label="有未保存修改"></span>
        <span v-if="tab.key === 'attribute' && attributeTabDirty" class="gx-mc__tab-dot" aria-label="有未保存修改"></span>
      </button>
    </nav>

    <div v-if="loadError" class="gx-mc__state-card gx-mc__state-card--error">
      <i class="el-icon-warning-outline" />
      <div class="gx-mc__state-body">
        <div class="gx-mc__state-title">搬家配置加载失败</div>
        <div class="gx-mc__state-text">{{ loadError }}</div>
      </div>
      <div class="gx-mc__state-actions">
        <el-button size="small" @click="requestBack">返回抖店列表</el-button>
        <el-button size="small" type="primary" :loading="loading" @click="refreshConfig">重新加载</el-button>
      </div>
    </div>

    <div v-else-if="shopsError && !shopId" class="gx-mc__state-card gx-mc__state-card--error">
      <i class="el-icon-warning-outline" />
      <div class="gx-mc__state-body">
        <div class="gx-mc__state-title">店铺列表加载失败</div>
        <div class="gx-mc__state-text">{{ shopsError }}</div>
      </div>
      <div class="gx-mc__state-actions">
        <el-button size="small" @click="requestBack">返回抖店列表</el-button>
        <el-button size="small" type="primary" :loading="shopsLoading" @click="loadShopOptions">重新加载</el-button>
      </div>
    </div>

    <div v-else-if="!shopId && !shopsLoading && !shops.length && !shopsError" class="gx-mc__state-card">
      <i class="el-icon-folder-opened" />
      <div class="gx-mc__state-body">
        <div class="gx-mc__state-title">暂无可用抖店</div>
        <div class="gx-mc__state-text">当前账号下没有可配置的抖店，请先在抖店列表完成授权。</div>
      </div>
      <div class="gx-mc__state-actions">
        <el-button size="small" @click="requestBack">返回抖店列表</el-button>
      </div>
    </div>

    <div v-else-if="!loaded" class="gx-mc__state-card">
      <i class="el-icon-loading" />
      <div class="gx-mc__state-body">
        <div class="gx-mc__state-title">{{ shopsLoading ? '正在加载店铺列表' : '正在加载搬家配置' }}</div>
        <div class="gx-mc__state-text">请稍候...</div>
      </div>
    </div>

    <template v-else>
      <div v-show="activeTab === 'basic'" class="gx-mc__tab-panel gx-mc__basic-layout">
        <aside class="gx-mc__basic-nav" aria-label="基本配置导航">
          <button type="button" class="gx-mc__basic-nav-item gx-mc__basic-nav-item--on">安装模式</button>
        </aside>
        <div class="gx-mc__basic-content">
          <section class="gx-mc__install-card">
            <div class="gx-mc__install-head">
              <h2 class="gx-mc__install-title">上门安装模式</h2>
              <p class="gx-mc__install-desc">按搬家配置模板设置商品上门安装服务方式</p>
              <el-link href="https://fxg.jinritemai.com/ffa/industry-service/privileges/home-installation?tab=template" target="_blank" rel="noopener noreferrer" type="primary" :underline="false">添加服务模板</el-link>
            </div>
            <div class="gx-mc__install-body">
              <el-alert v-if="isGlobalShop" type="info" :closable="false" title="系统预设属性店铺暂不支持安装模式配置。" />
              <template v-else>
                <el-alert v-if="installationError" class="gx-mc__install-alert" type="error" :closable="false" :title="installationError" />
                <el-radio-group v-model="installationModeValue" class="gx-mc__mode-options" :disabled="installationLoading || !installationLoaded || Boolean(installationError) || installationSaving" @change="markInstallationDirty">
                  <el-radio label="none">关闭</el-radio>
                  <el-radio label="cooperation">选择平台合作服务商</el-radio>
                  <el-radio label="selfSupport">线下提供免费上门安装</el-radio>
                  <el-radio label="noSupport">不提供上门安装</el-radio>
                </el-radio-group>
                <p v-if="installationLoading" class="gx-mc__install-tip"><i class="el-icon-loading" />正在读取当前模板的安装模式…</p>
                <p v-else class="gx-mc__install-tip"><i class="el-icon-info" />为提升商品转化及消费者购物体验，建议为用户提供上门安装服务。</p>
              </template>
              <div class="gx-mc__install-footer">
                <div class="gx-mc__install-footer-note">安装模式按当前店铺和搬家配置模板分别保存。</div>
                <div class="gx-mc__install-footer-actions">
                  <span class="gx-mc__footer-label">搬家配置模板</span>
                  <el-select v-model="templateSelectValue" :key="'basic-' + templateSelectKey" class="gx-mc__footer-template" filterable placeholder="请选择模板" :disabled="!templates.length || templateManageSaving || installationSaving">
                    <el-option v-for="item in templates" :key="item.id" :label="item.name" :value="Number(item.id)"><span>{{ item.name }}</span><span v-if="Number(item.is_default) === 1" class="gx-mc__default-mark">默认</span></el-option>
                  </el-select>
                  <el-button type="text" @click="openTemplateManage">管理模板<span v-if="templateManageHasChanges" class="gx-mc__tab-dot" aria-label="有未保存模板名称"></span></el-button>
                  <el-button type="primary" :loading="installationSaving" :disabled="!activeTemplateId || !installationLoaded || installationLoading || installationSaving || Boolean(installationError) || !installationDirty" @click="saveInstallationConfig">保存</el-button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div v-show="activeTab === 'attribute'" class="gx-mc__tab-panel gx-mc__attribute-tab">
        <aside class="gx-mc__inner-nav" aria-label="属性设置导航">
          <button
            v-for="section in attributeSections"
            :key="section.key"
            type="button"
            class="gx-mc__inner-nav-item"
            :class="{ 'gx-mc__inner-nav-item--on': activeAttributeSection === section.key }"
            @click="switchAttributeSection(section.key)"
          >
            <span>{{ section.label }}</span>
            <span v-if="section.key === 'attribute' && attributeDirty" class="gx-mc__tab-dot" aria-label="有未保存修改"></span>
            <span v-if="section.key === 'template' && templateManageHasChanges" class="gx-mc__tab-dot" aria-label="有未保存模板名称"></span>
          </button>
        </aside>

        <div class="gx-mc__inner-content">
          <div v-show="activeAttributeSection === 'basic'" class="gx-mc__inner-panel">
            <section class="gx-mc__dialog-card">
              <div class="gx-mc__dialog-head">
                <h2 class="gx-mc__dialog-title">{{ isGlobalShop ? '系统预设属性' : '类目属性设置' }}</h2>
                <div class="gx-mc__dialog-stats">
                  <span class="gx-mc__stat-badge">已设置 {{ activeConfiguredCount }} 个属性</span>
                  <span class="gx-mc__stat-badge">{{ isGlobalShop ? '有效叶子类目' : '已同步' }} {{ categoryCount }} 个类目</span>
                  <span v-if="!isGlobalShop" class="gx-mc__stat-badge">最近同步 {{ categorySyncedAtText }}</span>
                </div>
              </div>

              <el-alert
                v-if="shop.mer_shop_id && !productAppAvailable"
                class="gx-mc__alert"
                type="warning"
                title="抖店商品应用未授权或已失效，请先返回店铺管理重新授权。"
                show-icon
                :closable="false"
              />
              <el-alert
                v-if="syncTask && syncTask.status === 'failed'"
                class="gx-mc__alert"
                type="error"
                :title="syncTask.error_message || '上次抖店类目同步失败'"
                show-icon
                :closable="false"
              />

              <div class="gx-mc__dialog-actions">
                <el-button
                  type="primary"
                  class="gx-mc__dialog-primary"
                  :disabled="(!isGlobalShop && !activeTemplateId) || categoryCount === 0"
                  @click="switchAttributeSection('attribute')"
                >{{ isGlobalShop ? '配置系统预设属性' : '类目属性设置' }}</el-button>
                <el-button
                  v-if="!isGlobalShop"
                  class="gx-mc__dialog-secondary"
                  :loading="syncing"
                  :disabled="syncing || !productAppAvailable || attributeSaving || templateManageSaving"
                  @click="startCategorySync"
                >同步抖店类目</el-button>
                <el-button
                  v-if="!isGlobalShop"
                  class="gx-mc__dialog-secondary"
                  :loading="clearingAttributes"
                  :disabled="!activeTemplateId || activeConfiguredCount === 0 || attributeSaving || templateManageSaving"
                  @click="clearTemplateAttributes"
                >清空配置</el-button>
                <el-button
                  v-if="!isGlobalShop"
                  class="gx-mc__dialog-secondary"
                  :loading="globalImporting"
                  :disabled="!activeTemplateId || attributeSaving || templateManageSaving"
                  @click="importGlobalAttributes"
                >同步系统预设属性</el-button>
              </div>

              <p class="gx-mc__dialog-tip">
                <i class="el-icon-info" />
                {{ isGlobalShop
                  ? '这里配置的是系统预设属性，普通店铺需手动确认后覆盖到自己的模板。'
                  : '仅当当前显示的类目与抖店后台可经营类目不一致时，才需要同步。' }}
              </p>

              <div v-if="!isGlobalShop" class="gx-mc__dialog-footer">
                <div class="gx-mc__dialog-note">
                  <i class="el-icon-info" />
                  铺货时将使用设置为【默认】模板中的配置
                </div>
                <div class="gx-mc__dialog-footer-actions">
                  <span class="gx-mc__footer-label">搬家配置模板</span>
                  <el-select
                    v-model="templateSelectValue"
                    :key="'inner-basic-' + templateSelectKey"
                    class="gx-mc__footer-template"
                    filterable
                    placeholder="请选择模板"
                    :disabled="!templates.length || templateManageSaving || installationSaving"
                  >
                    <el-option
                      v-for="item in templates"
                      :key="item.id"
                      :label="item.name"
                      :value="Number(item.id)"
                    >
                      <span>{{ item.name }}</span>
                      <span v-if="Number(item.is_default) === 1" class="gx-mc__default-mark">默认</span>
                    </el-option>
                  </el-select>
                  <el-button type="text" @click="openTemplateManage">
                    管理模板
                    <span v-if="templateManageHasChanges" class="gx-mc__tab-dot" aria-label="有未保存模板名称"></span>
                  </el-button>
                  <el-button
                    type="primary"
                    :loading="syncTargetsLoading"
                    :disabled="!activeTemplateId || syncSaving || attributeSaving || templateManageSaving"
                    @click="saveAndSync"
                  >同步保存到多店</el-button>
                </div>
              </div>
            </section>
          </div>

          <div v-show="activeAttributeSection === 'attribute'" class="gx-mc__inner-panel">
            <div class="gx-mc__attribute-panel">
              <category-attribute-editor
                v-if="attributeEditorReady"
                ref="attributeEditor"
                embedded
                auto-init
                :shop-id="shopId"
                :template-id="activeTemplateId"
                :global-mode="isGlobalShop"
                @dirty-change="onAttributeDirtyChange"
                @saving-change="attributeSaving = $event"
                @category-change="attributeCanSave = $event"
                @saved="onAttributeSaved"
                @save-error="onAttributeSaveError"
              />
              <div v-else class="gx-mc__state-card">
                <i class="el-icon-loading" />
                <div class="gx-mc__state-body">
                  <div class="gx-mc__state-title">正在准备属性设置</div>
                </div>
              </div>
            </div>

            <div class="gx-mc__section-footer">
              <div class="gx-mc__section-note">
                <span v-if="!isGlobalShop">铺货时使用默认模板中的配置</span>
                <span v-else>系统预设属性店铺</span>
              </div>
              <div class="gx-mc__section-actions">
                <template v-if="!isGlobalShop">
                  <span class="gx-mc__footer-label">搬家配置模板</span>
                  <el-select
                    v-model="templateSelectValue"
                    :key="'inner-attr-' + templateSelectKey"
                    class="gx-mc__footer-template"
                    filterable
                    placeholder="请选择模板"
                    :disabled="!templates.length || templateManageSaving || installationSaving"
                  >
                    <el-option
                      v-for="item in templates"
                      :key="item.id"
                      :label="item.name"
                      :value="Number(item.id)"
                    >
                      <span>{{ item.name }}</span>
                      <span v-if="Number(item.is_default) === 1" class="gx-mc__default-mark">默认</span>
                    </el-option>
                  </el-select>
                  <el-button type="text" @click="openTemplateManage">
                    管理模板
                    <span v-if="templateManageHasChanges" class="gx-mc__tab-dot" aria-label="有未保存模板名称"></span>
                  </el-button>
                  <el-button
                    type="primary"
                    :loading="syncTargetsLoading"
                    :disabled="!activeTemplateId || syncSaving || attributeSaving || templateManageSaving"
                    @click="saveAndSync"
                  >保存并同步至多店</el-button>
                </template>
                <el-button
                  type="primary"
                  :loading="attributeSaving"
                  :disabled="!attributeCanSave || !attributeDirty"
                  @click="saveCurrentCategory"
                >保存</el-button>
              </div>
            </div>
          </div>

          <div v-if="!isGlobalShop" v-show="activeAttributeSection === 'template'" class="gx-mc__inner-panel">
            <div class="gx-mc__template-panel">
              <div class="gx-mc__panel-toolbar">
                <div class="gx-mc__panel-heading">
                  <h2 class="gx-mc__section-title">搬家配置模板</h2>
                  <p class="gx-mc__section-desc">铺货时使用默认模板中的配置；修改名称后点击“保存名称”。</p>
                </div>
                <div class="gx-mc__panel-actions">
                  <el-button size="small" :disabled="templateManageSaving || templateManageHasChanges" @click="createTemplate(false)">新建模板</el-button>
                  <el-button
                    size="small"
                    :disabled="!templateManageHasChanges || templateManageSaving"
                    @click="refreshTemplateManageRows"
                  >重置</el-button>
                  <el-button
                    size="small"
                    type="primary"
                    :loading="templateManageSaving"
                    :disabled="!templateManageHasChanges"
                    @click="saveManagedTemplateNames"
                  >保存名称</el-button>
                </div>
              </div>
              <el-table
                :data="templateManageRows"
                border
                max-height="520"
                empty-text="暂无模板"
              >
                <el-table-column label="模板名称" min-width="360">
                  <template slot-scope="scope">
                    <div class="template-name-cell">
                      <el-input
                        v-model="scope.row.name"
                        maxlength="50"
                        show-word-limit
                        :disabled="templateManageSaving"
                      />
                      <span v-if="Number(scope.row.is_default) === 1" class="default-template-mark">默认</span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="已配置属性" width="130" align="center">
                  <template slot-scope="scope">{{ Number(scope.row.configured_count || 0) }}</template>
                </el-table-column>
                <el-table-column label="操作" width="280">
                  <template slot-scope="scope">
                    <el-button
                      type="text"
                      :disabled="templateManageSaving || templateManageHasChanges || Number(scope.row.is_default) === 1"
                      @click="setDefaultTemplate(scope.row)"
                    >设为默认</el-button>
                    <el-button
                      type="text"
                      :disabled="templateManageSaving || templateManageHasChanges"
                      @click="copyManagedTemplate(scope.row)"
                    >复制模板</el-button>
                    <el-button
                      type="text"
                      :disabled="templateManageSaving || templateManageHasChanges || Number(scope.row.is_default) === 1 || templateManageRows.length <= 1"
                      @click="deleteTemplate(scope.row)"
                    >删除</el-button>
                  </template>
                </el-table-column>
              </el-table>
              <div class="gx-mc__section-footer gx-mc__template-footer">
                <div class="gx-mc__section-note">铺货时使用默认模板中的配置</div>
                <div class="gx-mc__section-actions">
                  <span class="gx-mc__footer-label">搬家配置模板</span>
                  <el-select
                    v-model="templateSelectValue"
                    :key="'inner-template-' + templateSelectKey"
                    class="gx-mc__footer-template"
                    filterable
                    placeholder="请选择模板"
                    :disabled="!templates.length || templateManageSaving || installationSaving"
                  >
                    <el-option
                      v-for="item in templates"
                      :key="item.id"
                      :label="item.name"
                      :value="Number(item.id)"
                    >
                      <span>{{ item.name }}</span>
                      <span v-if="Number(item.is_default) === 1" class="gx-mc__default-mark">默认</span>
                    </el-option>
                  </el-select>
                  <el-button
                    type="primary"
                    :loading="syncTargetsLoading"
                    :disabled="!activeTemplateId || syncSaving || attributeSaving || templateManageSaving"
                    @click="saveAndSync"
                  >保存并同步至多店</el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <el-dialog
      v-if="!isGlobalShop"
      title="同步搬家配置到多店"
      :visible.sync="syncDialogVisible"
      width="640px"
      :close-on-click-modal="false"
      :show-close="!syncSaving"
      :close-on-press-escape="!syncSaving"
      append-to-body
      class="custom-el-wrapper"
    >
      <div class="sync-dialog-tip">
        请选择目标店铺及搬家配置模板。同步后，目标模板的整套搬家配置将被当前模板覆盖。
      </div>
      <el-alert
        v-if="syncTargetsError"
        class="gx-mc__sync-error"
        type="error"
        :title="syncTargetsError"
        show-icon
        :closable="false"
      >
        <el-button type="text" @click="openTemplateSyncDialog">重新加载目标店铺</el-button>
      </el-alert>
      <el-table
        v-loading="syncTargetsLoading"
        :data="syncTargets"
        border
        max-height="420"
        empty-text="暂无其他抖店商品应用正常的店铺"
      >
        <el-table-column label="选择" width="70" align="center">
          <template slot-scope="scope">
            <el-checkbox v-model="scope.row.selected" :disabled="syncSaving" />
          </template>
        </el-table-column>
        <el-table-column prop="shop_name" label="店铺" min-width="190" />
        <el-table-column label="搬家配置模板" min-width="260">
          <template slot-scope="scope">
            <el-select
              v-model="scope.row.target_template_id"
              :disabled="!scope.row.selected || syncSaving"
              placeholder="请选择模板"
              class="sync-template-select"
            >
              <el-option
                v-for="template in scope.row.templates"
                :key="template.id"
                :label="template.name"
                :value="Number(template.id)"
              />
            </el-select>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button :disabled="syncSaving" @click="syncDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="syncSaving" @click="syncTemplateToShops">保存</el-button>
      </span>
    </el-dialog>
  </section>
</template>

<script>
import { mapActions } from 'vuex'

import GxLoading from '../../components/newUi/GxLoading.vue'
import CategoryAttributeEditor from './components/CategoryAttributeEditor.vue'

const SHOP_PAGE_SIZE = 1000

// 页面版搬家配置。配置动作与 DdMoveConfig.vue 保持一致；修改业务逻辑时请同步检查弹窗版本。
export default {
  name: 'DoudianMoveConfigPage',
  components: { GxLoading, CategoryAttributeEditor },
  props: {
    shopId: { type: Number, default: 0 }
  },
  data() {
    return {
      loading: false,
      loaded: false,
      loadError: '',
      requestSeq: 0,
      shops: [],
      shopsLoading: false,
      shopsError: '',
      shopListSeq: 0,
      selectedShopId: 0,
      shop: {},
      templates: [],
      activeTemplateId: '',
      categoryCount: 0,
      categorySyncedAt: 0,
      syncTask: null,
      syncTimer: null,
      activeTab: 'basic',
      activeAttributeSection: 'basic',
      attributeEditorReady: false,
      attributeDirty: false,
      attributeSaving: false,
      attributeCanSave: false,
      pendingSyncAfterSave: false,
      templateSelectKey: 0,
      templateManageSaving: false,
      templateManageRows: [],
      installationSeq: 0,
      installationLoading: false,
      installationLoaded: false,
      installationError: '',
      installationModeValue: 'none',
      installationDirty: false,
      installationSaving: false,
      clearingAttributes: false,
      syncTargetsSeq: 0,
      syncTargetsLoading: false,
      syncTargetsError: '',
      syncSaving: false,
      syncDialogVisible: false,
      syncTargets: [],
      isGlobalShop: false,
      globalConfiguredAttributeCount: 0,
      globalImporting: false
    }
  },
  computed: {
    activeTemplate() {
      return this.templates.find(item => Number(item.id) === Number(this.activeTemplateId)) || {}
    },
    defaultTemplate() {
      return this.templates.find(item => Number(item.is_default) === 1) || {}
    },
    activeConfiguredCount() {
      if (this.isGlobalShop) return this.globalConfiguredAttributeCount
      return Number(this.activeTemplate.configured_count || 0)
    },
    syncing() {
      return Boolean(this.syncTask && ['pending', 'running'].includes(this.syncTask.status))
    },
    productAppAvailable() {
      return Number(this.shop.app_product_status || 0) === 1
    },
    categorySyncedAtText() {
      const timestamp = Number(this.categorySyncedAt || 0)
      if (!timestamp) return '暂未同步'
      const date = new Date(timestamp * 1000)
      const pad = value => String(value).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    selectedShop() {
      return this.shops.find(item => Number(item.shop_id) === Number(this.selectedShopId)) || {}
    },
    activeShopName() {
      return this.selectedShop.shop_name || this.shop.shop_name || ''
    },
    templateManageHasChanges() {
      return this.templateManageRows.some(item => String(item.name || '') !== String(item.original_name || ''))
    },
    topTabs() {
      return [
        { key: 'basic', label: '基本配置' },
        { key: 'attribute', label: '属性设置' }
      ]
    },
    attributeSections() {
      const sections = [
        { key: 'basic', label: '基础配置' },
        { key: 'attribute', label: '属性设置' }
      ]
      if (!this.isGlobalShop) sections.push({ key: 'template', label: '模板管理' })
      return sections
    },
    attributeTabDirty() {
      return Boolean(this.attributeDirty || this.templateManageHasChanges)
    },
    templateSelectValue: {
      get() {
        return this.activeTemplateId
      },
      set(value) {
        this.requestTemplateChange(value)
      }
    }
  },
  watch: {
    shopId(value) {
      const id = Number(value || 0)
      this.selectedShopId = id
      if (id > 0) {
        this.handleShopSwitch(id)
      } else {
        this.stopPolling()
        this.resetConfigState()
        this.ensureCurrentShop()
      }
    },
    activeTemplateId() {
      this.loadInstallationConfig()
    }
  },
  created() {
    this.selectedShopId = Number(this.shopId) || 0
    this.loadShopOptions()
    if (this.shopId > 0) this.loadConfig(false)
  },
  mounted() {
    this.scrollContentTop()
  },
  beforeDestroy() {
    this.requestSeq += 1
    this.shopListSeq += 1
    this.stopPolling()
    this.syncTask = null
  },
  methods: {
    ...mapActions({
      getShopList: 'ucenter/getDdShopList',
      getConfig: 'ucenter/getDoudianMoveConfig',
      createTemplateAction: 'ucenter/createDoudianMoveTemplate',
      renameTemplateAction: 'ucenter/renameDoudianMoveTemplate',
      setDefaultTemplateAction: 'ucenter/setDefaultDoudianMoveTemplate',
      deleteTemplateAction: 'ucenter/deleteDoudianMoveTemplate',
      clearTemplateCategoryConfigs: 'ucenter/clearDoudianMoveTemplateCategoryConfigs',
      getInstallationConfig: 'ucenter/getDoudianMoveTemplateInstallation',
      saveInstallation: 'ucenter/saveDoudianMoveTemplateInstallation',
      syncCategories: 'ucenter/syncDoudianMoveCategories',
      getSyncTask: 'ucenter/getDoudianMoveCategorySyncTask',
      getTemplateSyncTargets: 'ucenter/getDoudianMoveTemplateSyncTargets',
      syncTemplate: 'ucenter/syncDoudianMoveTemplate',
      getGlobalConfigPreview: 'ucenter/getDoudianMoveGlobalConfigPreview',
      importGlobalConfig: 'ucenter/importDoudianMoveGlobalConfig'
    }),

    isCurrentShop(shopId) {
      return Number(this.shopId || 0) === Number(shopId || 0)
    },

    loadShopOptions() {
      const seq = ++this.shopListSeq
      this.shopsLoading = true
      this.shopsError = ''
      const hadShops = this.shops.length
      this.getShopList({
        data: { shop_type: 1, page: 1, page_size: SHOP_PAGE_SIZE },
        success: res => {
          if (seq !== this.shopListSeq) return
          this.shopsLoading = false
          if (res.code !== 200) {
            this.shopsError = res.msg || '店铺列表加载失败'
            if (!hadShops) this.ensureCurrentShop()
            return
          }
          const data = res.data || {}
          let rawList = []
          if (data.list && Array.isArray(data.list.list)) {
            rawList = data.list.list
          } else if (Array.isArray(data.list)) {
            rawList = data.list
          } else if (Array.isArray(data)) {
            rawList = data
          }
          this.shops = rawList.filter(item => item && item.shop_id)
          this.ensureCurrentShop()
        },
        fail: () => {
          if (seq !== this.shopListSeq) return
          this.shopsLoading = false
          this.shopsError = '店铺列表加载失败，请稍后重试'
          if (!hadShops) this.ensureCurrentShop()
        }
      })
    },

    ensureCurrentShop() {
      if (!this.shops.length) {
        if (this.shop.mer_shop_id) {
          this.shops = [{
            shop_id: Number(this.shop.mer_shop_id),
            shop_name: this.shop.shop_name || `店铺 ${this.shop.mer_shop_id}`,
            group_name: '当前店铺',
            app_product_status: this.shop.app_product_status
          }]
          this.selectedShopId = Number(this.shop.mer_shop_id)
        }
        return
      }

      const currentId = Number(this.shopId || this.selectedShopId || 0)
      const current = this.shops.find(item => Number(item.shop_id) === currentId)
      if (current) {
        this.selectedShopId = currentId
        return
      }

      if (currentId > 0 && this.loading) return

      if (currentId > 0 && this.shop.mer_shop_id && Number(this.shop.mer_shop_id) === currentId) {
        this.shops.unshift({
          shop_id: currentId,
          shop_name: this.shop.shop_name || `店铺 ${currentId}`,
          group_name: '当前店铺',
          app_product_status: this.shop.app_product_status
        })
        this.selectedShopId = currentId
        return
      }

      const firstShopId = Number(this.shops[0].shop_id)
      this.selectedShopId = firstShopId
      if (currentId > 0 && currentId !== firstShopId) {
        this.$message.warning('当前店铺不可用，已切换到第一家店铺')
      }
      if (String(this.$route.query.move_shop_id || '') === String(firstShopId)) {
        this.handleShopSwitch(firstShopId)
      } else {
        this.replaceShopQuery(firstShopId)
      }
    },

    replaceShopQuery(shopId) {
      if (String(this.$route.query.move_shop_id || '') === String(shopId)) return
      const fallbackShopId = Number(this.shopId) || 0
      this.$router.replace({
        path: this.$route.path,
        query: { ...this.$route.query, move_shop_id: String(shopId) }
      }).catch(() => {
        this.selectedShopId = fallbackShopId
      })
    },

    onShopSelect(shopId) {
      const id = Number(shopId || 0)
      const currentId = Number(this.shopId || 0)
      if (!id || id === currentId) {
        this.selectedShopId = currentId
        return
      }
      const proceed = () => {
        this.replaceShopQuery(id)
      }
      if (!this.hasUnsavedChanges()) {
        proceed()
        return
      }
      this.$confirm('当前存在未保存的配置，切换店铺后将丢失，是否继续？', '提示', { type: 'warning' })
        .then(proceed)
        .catch(() => {
          this.selectedShopId = currentId
        })
    },

    hasUnsavedChanges() {
      return Boolean(this.attributeDirty || this.templateManageHasChanges || this.installationDirty)
    },

    discardAttributeDirty() {
      this.attributeDirty = false
      this.installationDirty = false
      if (this.$refs.attributeEditor) this.$refs.attributeEditor.discardDirty()
    },

    requestBack() {
      if (!this.hasUnsavedChanges()) {
        this.$emit('back')
        return
      }
      this.$confirm('当前存在未保存的配置，离开后将丢失，是否继续？', '提示', { type: 'warning' })
        .then(() => {
          this.discardAttributeDirty()
          this.$emit('back')
        })
        .catch(() => {})
    },

    switchTab(key) {
      this.activeTab = key
      if (key === 'attribute') {
        if (this.activeAttributeSection === 'attribute') this.attributeEditorReady = true
        if (this.activeAttributeSection === 'template' && !this.templateManageHasChanges) this.refreshTemplateManageRows()
      }
      this.scrollContentTop()
    },

    switchAttributeSection(key) {
      this.activeAttributeSection = key
      if (key === 'attribute') this.attributeEditorReady = true
      if (key === 'template' && !this.templateManageHasChanges) this.refreshTemplateManageRows()
      this.scrollContentTop()
    },

    requestTemplateChange(value) {
      const id = Number(value || 0)
      if (!id || id === Number(this.activeTemplateId)) return
      const proceed = () => {
        this.discardAttributeDirty()
        this.activeTemplateId = id
        this.refreshTemplateManageRows()
        this.loadConfig()
      }
      if (!this.hasUnsavedChanges()) {
        proceed()
        return
      }
      this.$confirm('当前存在未保存的配置，切换模板后将丢失，是否继续？', '提示', { type: 'warning' })
        .then(proceed)
        .catch(() => {
          this.templateSelectKey += 1
        })
    },

    resetInstallationState() {
      this.installationSeq += 1
      this.installationLoading = false
      this.installationLoaded = false
      this.installationError = ''
      this.installationModeValue = 'none'
      this.installationDirty = false
      this.installationSaving = false
    },

    loadInstallationConfig() {
      this.resetInstallationState()
      const seq = ++this.installationSeq
      if (!this.shopId || !this.activeTemplateId || this.isGlobalShop) return
      const templateId = Number(this.activeTemplateId)
      const shopId = Number(this.shopId || 0)
      this.installationLoading = true
      this.getInstallationConfig({
        templateId,
        success: res => {
          if (seq !== this.installationSeq || templateId !== Number(this.activeTemplateId) || !this.isCurrentShop(shopId)) return
          this.installationLoading = false
          if (res.code !== 200) {
            this.installationError = res.msg || '读取安装模式失败'
            this.installationLoaded = true
            return
          }
          const data = res.data && typeof res.data === 'object' ? res.data : res
          if (!Object.prototype.hasOwnProperty.call(data, 'installation_mode')) {
            this.installationError = '安装模式数据缺失，请刷新后重试'
            this.installationLoaded = true
            return
          }
          const mode = String(data.installation_mode)
          if (!['none', 'cooperation', 'selfSupport', 'noSupport'].includes(mode)) {
            this.installationError = '返回的安装模式无效，请刷新后重试'
            this.installationLoaded = true
            return
          }
          this.installationModeValue = mode
          this.installationLoaded = true
        },
        fail: () => {
          if (seq !== this.installationSeq || templateId !== Number(this.activeTemplateId) || !this.isCurrentShop(shopId)) return
          this.installationLoading = false
          this.installationLoaded = true
          this.installationError = '读取安装模式失败，请稍后重试'
        }
      })
    },

    markInstallationDirty() {
      this.installationDirty = true
    },

    saveInstallationConfig() {
      if (this.installationSaving || this.installationLoading || this.installationError || !this.installationLoaded || !['none', 'cooperation', 'selfSupport', 'noSupport'].includes(this.installationModeValue) || !this.activeTemplateId) return
      const templateId = Number(this.activeTemplateId)
      const shopId = Number(this.shopId || 0)
      const seq = this.installationSeq
      const data = { installation_mode: String(this.installationModeValue) }
      this.installationSaving = true
      this.saveInstallation({
        templateId,
        data,
        success: res => {
          if (seq !== this.installationSeq || templateId !== Number(this.activeTemplateId) || !this.isCurrentShop(shopId)) return
          this.installationSaving = false
          if (res.code !== 200) return this.$message.error(res.msg || '保存安装模式失败')
          this.installationDirty = false
          this.$message.success(res.msg || '安装模式已保存')
        },
        fail: () => {
          if (seq !== this.installationSeq || templateId !== Number(this.activeTemplateId) || !this.isCurrentShop(shopId)) return
          this.installationSaving = false
          this.$message.error('保存安装模式失败，请稍后重试')
        }
      })
    },

    onAttributeDirtyChange(dirty) {
      this.attributeDirty = Boolean(dirty)
    },

    refreshAttributeEditor() {
      if (this.attributeEditorReady && this.$refs.attributeEditor) {
        this.$refs.attributeEditor.initialize()
      }
    },

    saveCurrentCategory() {
      if (this.attributeSaving) return
      if (this.$refs.attributeEditor) this.$refs.attributeEditor.saveConfig()
    },

    saveAndSync() {
      if (!this.activeTemplateId) return
      if (this.attributeDirty) {
        this.pendingSyncAfterSave = true
        this.saveCurrentCategory()
        return
      }
      this.openTemplateSyncDialog()
    },

    onAttributeSaved() {
      const shouldSync = this.pendingSyncAfterSave
      this.pendingSyncAfterSave = false
      this.attributeDirty = false
      this.loadConfig()
      if (shouldSync) this.openTemplateSyncDialog()
    },

    onAttributeSaveError() {
      this.pendingSyncAfterSave = false
    },

    handleShopSwitch() {
      this.stopPolling()
      this.resetConfigState()
      this.loadConfig(false)
      this.scrollContentTop()
    },

    resetConfigState() {
      this.requestSeq += 1
      this.shop = {}
      this.templates = []
      this.activeTemplateId = ''
      this.categoryCount = 0
      this.categorySyncedAt = 0
      this.syncTask = null
      this.isGlobalShop = false
      this.globalConfiguredAttributeCount = 0
      this.loading = false
      this.loaded = false
      this.loadError = ''
      this.clearingAttributes = false
      this.globalImporting = false
      this.attributeDirty = false
      this.attributeEditorReady = false
      this.attributeSaving = false
      this.attributeCanSave = false
      this.pendingSyncAfterSave = false
      this.templateManageSaving = false
      this.templateManageRows = []
      this.resetInstallationState()
      this.syncTargetsSeq += 1
      this.syncTargetsLoading = false
      this.syncTargetsError = ''
      this.syncSaving = false
      this.syncDialogVisible = false
      this.syncTargets = []
    },

    scrollContentTop() {
      this.$nextTick(() => {
        const main = document.getElementById('gx-main')
        if (main) main.scrollTop = 0
      })
    },

    loadConfig(keepActive = true) {
      const shopId = Number(this.shopId || 0)
      if (!shopId) {
        this.loading = false
        this.loaded = false
        this.loadError = ''
        return
      }

      const hadLoaded = this.loaded
      const seq = ++this.requestSeq
      const previousId = keepActive ? Number(this.activeTemplateId) : 0
      this.loading = true
      this.loadError = ''

      this.getConfig({
        shopId,
        success: res => {
          if (seq !== this.requestSeq) return
          this.loading = false
          if (res.code !== 200) {
            if (hadLoaded) {
              this.$message.error(res.msg || '获取搬家配置失败')
              return
            }
            this.loaded = false
            this.loadError = res.msg || '获取搬家配置失败'
            this.ensureCurrentShop()
            return
          }

          const data = res.data || {}
          if (!data.shop || !data.shop.mer_shop_id) {
            this.loaded = false
            this.loadError = '未获取到店铺信息，请返回抖店列表重新进入'
            this.ensureCurrentShop()
            return
          }

          this.shop = data.shop
          this.templates = Array.isArray(data.templates) ? data.templates : []
          this.categoryCount = Number(data.category_count || 0)
          this.categorySyncedAt = Number(data.category_synced_at || 0)
          this.syncTask = data.sync_task || null
          this.isGlobalShop = Boolean(data.is_global_attribute_shop)
          this.globalConfiguredAttributeCount = Number(data.configured_attribute_count || 0)
          const activeExists = this.templates.some(item => Number(item.id) === previousId)
          this.activeTemplateId = activeExists
            ? previousId
            : Number(data.default_template_id || (this.templates[0] || {}).id || 0)
          this.loaded = true
          this.loadError = ''
          if (!hadLoaded) this.scrollContentTop()
          if (this.isGlobalShop && this.activeAttributeSection === 'template') this.activeAttributeSection = 'basic'
          if (this.activeTab === 'attribute' && this.activeAttributeSection === 'attribute') this.attributeEditorReady = true
          if (this.activeTab === 'attribute' && this.activeAttributeSection === 'template' && !this.templateManageHasChanges) this.refreshTemplateManageRows()
          if (this.syncing) this.startPolling()
          this.ensureCurrentShop()
        },
        fail: () => {
          if (seq !== this.requestSeq) return
          this.loading = false
          if (hadLoaded) {
            this.$message.error('获取搬家配置失败，请稍后重试')
            return
          }
          this.loaded = false
          this.loadError = '获取搬家配置失败，请稍后重试'
          this.ensureCurrentShop()
        }
      })
    },

    refreshConfig() {
      if (this.loading) return
      if (this.installationDirty) {
        this.$confirm('当前有未保存的配置，刷新后将丢失，是否继续？', '提示', { type: 'warning' })
          .then(() => this.refreshConfigData())
          .catch(() => {})
        return
      }
      this.refreshConfigData()
    },

    refreshConfigData() {
      if (this.shopId > 0) {
        if (this.shopsError) this.loadShopOptions()
        this.loadConfig()
        this.loadInstallationConfig()
      } else {
        this.loadShopOptions()
      }
    },

    refreshTemplateManageRows() {
      this.templateManageRows = this.templates.map(item => ({
        ...item,
        original_name: item.name
      }))
    },

    openTemplateManage() {
      this.activeTab = 'attribute'
      this.activeAttributeSection = 'template'
      if (!this.templateManageHasChanges) this.refreshTemplateManageRows()
      this.scrollContentTop()
    },

    copyManagedTemplate(template) {
      this.createTemplate(true, template)
    },

    saveManagedTemplateNames() {
      const actionShopId = Number(this.shopId || 0)
      const normalizedRows = this.templateManageRows.map(item => ({
        ...item,
        name: String(item.name || '').trim()
      }))
      const invalidRow = normalizedRows.find(item => item.name.length === 0 || item.name.length > 50)
      if (invalidRow) return this.$message.error('模板名称长度应为1至50个字符')
      const names = normalizedRows.map(item => item.name)
      if (new Set(names).size !== names.length) return this.$message.error('模板名称不能重复')

      const changedRows = normalizedRows.filter(item => item.name !== item.original_name)
      if (changedRows.length === 0) return

      this.templateManageSaving = true
      const saveNext = index => {
        if (!this.isCurrentShop(actionShopId)) return
        if (index >= changedRows.length) {
          this.templateManageSaving = false
          this.$message.success('模板名称已更新')
          this.loadConfig()
          return
        }
        const item = changedRows[index]
        this.renameTemplateAction({
          templateId: item.id,
          data: { name: item.name },
          success: res => {
            if (!this.isCurrentShop(actionShopId)) return
            if (res.code !== 200) {
              this.templateManageSaving = false
              this.$message.error(res.msg || '重命名失败')
              return
            }
            const sourceRow = this.templateManageRows.find(row => Number(row.id) === Number(item.id))
            if (sourceRow) sourceRow.original_name = item.name
            saveNext(index + 1)
          },
          fail: () => {
            if (!this.isCurrentShop(actionShopId)) return
            this.templateManageSaving = false
            this.$message.error('模板名称更新失败，请稍后重试')
          }
        })
      }
      saveNext(0)
    },

    startCategorySync() {
      if (!this.productAppAvailable) {
        this.$message.error('抖店商品应用未授权或已失效，请先返回抖店列表重新授权')
        return
      }
      const dirtyTip = this.attributeDirty ? '当前属性设置中有未保存的修改，同步完成后属性面板会刷新。' : ''
      this.$confirm(
        `同步后将以抖店后台当前可经营类目更新本地类目库。仅在当前显示类目与抖店后台不一致时执行，${dirtyTip}是否继续？`,
        '同步抖店类目',
        {
          type: 'warning',
          confirmButtonText: '继续同步',
          cancelButtonText: '取消'
        }
      ).then(() => {
        const actionShopId = Number(this.shopId || 0)
        this.syncCategories({
          shopId: this.shopId,
          success: res => {
            if (!this.isCurrentShop(actionShopId)) return
            if (res.code !== 200) {
              this.$message.error(res.msg || '提交同步任务失败')
              return
            }
            this.syncTask = res.data
            this.$message.success(res.msg || '类目同步任务已提交')
            this.startPolling()
          },
          fail: () => {
            if (!this.isCurrentShop(actionShopId)) return
            this.$message.error('提交同步任务失败，请稍后重试')
          }
        })
      }).catch(() => {})
    },

    startPolling() {
      this.stopPolling()
      this.pollSyncTask()
    },

    stopPolling() {
      if (this.syncTimer) clearTimeout(this.syncTimer)
      this.syncTimer = null
    },

    pollSyncTask() {
      if (!this.syncTask || !this.syncTask.id) return
      const taskId = this.syncTask.id
      const shopId = Number(this.shopId || 0)
      this.getSyncTask({
        taskId,
        success: res => {
          if (this._isDestroyed) return
          if (Number(this.shopId || 0) !== shopId) return
          if (!this.syncTask || Number(this.syncTask.id) !== Number(taskId)) return
          if (res.code !== 200) {
            this.stopPolling()
            this.$message.error(res.msg || '获取类目同步任务失败')
            return
          }
          if (!res.data) {
            this.stopPolling()
            this.$message.error('获取类目同步任务失败，请稍后重试')
            return
          }
          this.syncTask = res.data
          if (!['pending', 'running'].includes(this.syncTask.status)) {
            this.stopPolling()
            if (this.syncTask.status === 'success') {
              this.$message.success('抖店类目同步完成')
              this.loadConfig()
              this.refreshAttributeEditor()
            } else {
              this.$message.error(this.syncTask.error_message || '抖店类目同步失败')
            }
            return
          }
          this.syncTimer = setTimeout(this.pollSyncTask, 2000)
        },
        fail: () => {
          if (this._isDestroyed) return
          if (Number(this.shopId || 0) !== shopId) return
          this.stopPolling()
          this.$message.error('获取类目同步任务失败，请稍后重试')
        }
      })
    },

    createTemplate(copyCurrent, sourceTemplate = this.activeTemplate) {
      if (this.templateManageSaving) return
      if (this.templateManageHasChanges) {
        this.$message.warning('请先保存或重置模板名称修改')
        return
      }
      const actionShopId = Number(this.shopId || 0)
      const title = copyCurrent ? '复制模板' : '新建模板'
      const defaultName = copyCurrent ? `${sourceTemplate.name || '配置'}-副本` : ''
      this.$prompt('请输入模板名称', title, {
        inputValue: defaultName,
        inputValidator: value => {
          const name = String(value || '').trim()
          return name.length > 0 && name.length <= 50 ? true : '模板名称长度应为1至50个字符'
        }
      }).then(({ value }) => {
        if (!this.isCurrentShop(actionShopId)) return
        this.createTemplateAction({
          shopId: actionShopId,
          data: {
            name: String(value).trim(),
            copy_from_template_id: copyCurrent ? Number(sourceTemplate.id || 0) : 0
          },
          success: res => {
            if (!this.isCurrentShop(actionShopId)) return
            if (res.code !== 200) {
              this.$message.error(res.msg || '模板创建失败')
              return
            }
            this.activeTemplateId = Number((res.data || {}).id || 0)
            this.$message.success(res.msg || '模板创建成功')
            this.loadConfig()
          },
          fail: () => {
            if (!this.isCurrentShop(actionShopId)) return
            this.$message.error('模板创建失败，请稍后重试')
          }
        })
      }).catch(() => {})
    },

    setDefaultTemplate(template = this.activeTemplate) {
      if (!template || !template.id) return
      const actionShopId = Number(this.shopId || 0)
      this.$confirm(`确认将“${template.name}”设为默认模板？`, '提示', { type: 'warning' })
        .then(() => {
          if (!this.isCurrentShop(actionShopId)) return
          this.setDefaultTemplateAction({
            templateId: template.id,
            success: res => {
              if (!this.isCurrentShop(actionShopId)) return
              if (res.code !== 200) return this.$message.error(res.msg || '设置失败')
              this.$message.success(res.msg || '默认模板已更新')
              this.loadConfig()
            },
            fail: () => {
              if (!this.isCurrentShop(actionShopId)) return
              this.$message.error('设置默认模板失败，请稍后重试')
            }
          })
        }).catch(() => {})
    },

    deleteTemplate(template = this.activeTemplate) {
      if (!template || !template.id) return
      const actionShopId = Number(this.shopId || 0)
      this.$confirm(`删除模板“${template.name}”后，其类目属性配置也会删除。是否继续？`, '提示', { type: 'warning' })
        .then(() => {
          if (!this.isCurrentShop(actionShopId)) return
          this.deleteTemplateAction({
            templateId: template.id,
            success: res => {
              if (!this.isCurrentShop(actionShopId)) return
              if (res.code !== 200) return this.$message.error(res.msg || '删除失败')
              const deletingActive = Number(template.id) === Number(this.activeTemplateId)
              if (deletingActive) this.activeTemplateId = ''
              this.$message.success(res.msg || '模板已删除')
              this.loadConfig(!deletingActive)
            },
            fail: () => {
              if (!this.isCurrentShop(actionShopId)) return
              this.$message.error('删除模板失败，请稍后重试')
            }
          })
        }).catch(() => {})
    },

    clearTemplateAttributes() {
      if (!this.activeTemplateId) return
      const actionShopId = Number(this.shopId || 0)
      this.$confirm(
        `确认清空模板“${this.activeTemplate.name}”的全部类目属性配置？清空后无法恢复。`,
        '清空配置',
        {
          type: 'warning',
          confirmButtonText: '确认清空',
          cancelButtonText: '取消'
        }
      ).then(() => {
        if (!this.isCurrentShop(actionShopId)) return
        this.clearingAttributes = true
        this.clearTemplateCategoryConfigs({
          templateId: this.activeTemplateId,
          success: res => {
            if (!this.isCurrentShop(actionShopId)) return
            this.clearingAttributes = false
            if (res.code !== 200) return this.$message.error(res.msg || '清空配置失败')
            this.$message.success(res.msg || '配置已清空')
            this.loadConfig()
            this.refreshAttributeEditor()
          },
          fail: () => {
            if (!this.isCurrentShop(actionShopId)) return
            this.clearingAttributes = false
            this.$message.error('清空配置失败，请稍后重试')
          }
        })
      }).catch(() => {})
    },

    importGlobalAttributes() {
      if (!this.activeTemplateId) return
      const actionShopId = Number(this.shopId || 0)
      this.globalImporting = true
      this.getGlobalConfigPreview({
        templateId: this.activeTemplateId,
        success: res => {
          if (!this.isCurrentShop(actionShopId)) return
          this.globalImporting = false
          if (res.code !== 200) return this.$message.error(res.msg || '获取系统预设属性预览失败')
          const data = res.data || {}
          if (!Number(data.importable_count || 0)) {
            return this.$message.warning('当前店铺没有可同步的系统预设属性')
          }
          const message = `同步系统预设属性会覆盖当前模板已配置的类目属性。系统已配置 ${data.global_config_count || 0} 个类目，当前店铺可同步 ${data.importable_count || 0} 个，并将清除 ${data.will_clear_count || 0} 个不在同步范围内的类目配置。是否继续？`
          this.$confirm(message, '二次确认', {
            type: 'warning',
            confirmButtonText: '确认同步',
            cancelButtonText: '取消'
          }).then(() => this.executeGlobalImport(actionShopId)).catch(() => {})
        },
        fail: () => {
          if (!this.isCurrentShop(actionShopId)) return
          this.globalImporting = false
          this.$message.error('获取系统预设属性预览失败，请稍后重试')
        }
      })
    },

    executeGlobalImport(actionShopId = Number(this.shopId || 0)) {
      if (!this.isCurrentShop(actionShopId)) return
      this.globalImporting = true
      this.importGlobalConfig({
        templateId: this.activeTemplateId,
        data: { confirmed: true },
        success: res => {
          if (!this.isCurrentShop(actionShopId)) return
          this.globalImporting = false
          if (res.code !== 200) return this.$message.error(res.msg || '同步系统预设属性失败')
          this.$message.success(res.msg || '系统预设属性已同步到当前模板')
          this.loadConfig()
          this.refreshAttributeEditor()
        },
        fail: () => {
          if (!this.isCurrentShop(actionShopId)) return
          this.globalImporting = false
          this.$message.error('同步系统预设属性失败，请稍后重试')
        }
      })
    },

    openTemplateSyncDialog() {
      if (!this.activeTemplateId) return
      const seq = ++this.syncTargetsSeq
      this.syncDialogVisible = true
      this.syncTargetsLoading = true
      this.syncTargets = []
      this.syncTargetsError = ''
      this.getTemplateSyncTargets({
        templateId: this.activeTemplateId,
        success: res => {
          if (seq !== this.syncTargetsSeq) return
          this.syncTargetsLoading = false
          if (res.code !== 200) {
            this.syncTargetsError = res.msg || '获取目标店铺失败'
            this.$message.error(this.syncTargetsError)
            return
          }
          const targets = Array.isArray(res.data) ? res.data : []
          this.syncTargets = targets.map(shop => {
            const templates = Array.isArray(shop.templates) ? shop.templates : []
            const defaultTemplate = templates.find(template => Number(template.is_default) === 1)
            return {
              ...shop,
              selected: false,
              target_template_id: Number((defaultTemplate || templates[0] || {}).id || 0)
            }
          })
        },
        fail: () => {
          if (seq !== this.syncTargetsSeq) return
          this.syncTargetsLoading = false
          this.syncTargetsError = '获取目标店铺失败，请稍后重试'
          this.$message.error(this.syncTargetsError)
        }
      })
    },

    syncTemplateToShops() {
      const actionShopId = Number(this.shopId || 0)
      const selected = this.syncTargets.filter(shop => shop.selected)
      if (selected.length === 0) return this.$message.error('请选择需要同步的店铺')
      if (selected.some(shop => !shop.target_template_id)) {
        return this.$message.error('请选择目标店铺的搬家配置模板')
      }
      this.$confirm(
        `确认将“${this.activeTemplate.name || '当前模板'}”的整套搬家配置覆盖到选中的 ${selected.length} 个店铺模板？`,
        '同步搬家配置',
        {
          type: 'warning',
          confirmButtonText: '确认同步',
          cancelButtonText: '取消'
        }
      ).then(() => {
        if (!this.isCurrentShop(actionShopId)) return
        this.syncSaving = true
        this.syncTemplate({
          templateId: this.activeTemplateId,
          data: {
            target_template_ids: selected.map(shop => shop.target_template_id)
          },
          success: res => {
            if (!this.isCurrentShop(actionShopId)) return
            this.syncSaving = false
            if (res.code !== 200) return this.$message.error(res.msg || '同步失败')
            this.syncDialogVisible = false
            this.$message.success(res.msg || '搬家配置同步成功')
          },
          fail: () => {
            if (!this.isCurrentShop(actionShopId)) return
            this.syncSaving = false
            this.$message.error('同步失败，请稍后重试')
          }
        })
      }).catch(() => {})
    }
  }
}
</script>


<style scoped>
.gx-mc {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 96px);
  color: #1A1A2E;
  font-family: 'Noto Sans SC', sans-serif;
}

.gx-mc__head {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 2px 0 14px;
  border-bottom: 1px solid #E0E0EC;
}

.gx-mc__back {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #FFCCC7;
  border-radius: 5px;
  background: #FFF5F5;
  color: #C8443C;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  transition: all .2s;
}

.gx-mc__back:hover {
  border-color: #C8443C;
  background: #FFECEC;
}

.gx-mc__back-icon {
  font-size: 16px;
  line-height: 1;
}

.gx-mc__heading {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}

.gx-mc__title {
  margin: 0;
  color: #1A1A2E;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
}

.gx-mc__shop {
  max-width: 300px;
  overflow: hidden;
  padding: 3px 10px;
  border-radius: 12px;
  background: #F4F6F8;
  color: #4A4A6A;
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gx-mc__plat {
  padding: 3px 9px;
  border: 1px solid rgba(212, 169, 70, .34);
  border-radius: 12px;
  background: #FFFBF0;
  color: #A67C1A;
  font-size: 12px;
  line-height: 18px;
}

.gx-mc__head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

.gx-mc__shop-label {
  color: #4A4A6A;
  font-size: 12px;
  white-space: nowrap;
}

.gx-mc__shop-select {
  width: 260px;
}

.gx-mc__refresh {
  height: 32px;
  padding: 0 14px;
  border: 1px solid #E0E0EC;
  border-radius: 5px;
  background: #FFFFFF;
  color: #4A4A6A;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  transition: all .2s;
}

.gx-mc__refresh:hover:not(:disabled) {
  border-color: rgba(212, 169, 70, .45);
  color: #A67C1A;
  background: #FFFBF0;
}

.gx-mc__refresh:disabled {
  cursor: not-allowed;
  opacity: .55;
}

.gx-mc__inline-error {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding: 8px 12px;
  border: 1px solid #FBC4C0;
  border-radius: 6px;
  background: #FFF5F5;
  color: #C8443C;
  font-size: 12px;
}

.gx-mc__inline-error button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #C8443C;
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
}

.gx-mc__tabs {
  position: sticky;
  top: 0;
  z-index: 12;
  display: flex;
  align-items: stretch;
  height: 45px;
  margin-top: 12px;
  border-bottom: 1px solid #E0E0EC;
  background: #FFFFFF;
  overflow-x: auto;
  scrollbar-width: none;
}

.gx-mc__tabs::-webkit-scrollbar {
  display: none;
}

.gx-mc__tab {
  position: relative;
  flex: none;
  padding: 0 20px;
  border: 0;
  background: transparent;
  color: #4A4A6A;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  white-space: nowrap;
}

.gx-mc__tab:hover {
  color: #C8443C;
}

.gx-mc__tab--on {
  color: #C8443C;
  font-weight: 600;
}

.gx-mc__tab--on::after {
  position: absolute;
  right: 16px;
  bottom: -1px;
  left: 16px;
  height: 2px;
  border-radius: 2px;
  background: #C8443C;
  content: '';
}

.gx-mc__tab-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-left: 5px;
  border-radius: 50%;
  background: #C8443C;
  vertical-align: middle;
}

.gx-mc__tab-panel {
  min-width: 0;
}

.gx-mc__basic-panel {
  display: flex;
  align-items: flex-start;
  margin-top: 14px;
  border: 1px solid #E0E0EC;
  border-radius: 8px;
  background: #FFFFFF;
}

.gx-mc__subnav {
  position: sticky;
  top: 45px;
  flex: 0 0 180px;
  width: 180px;
  align-self: flex-start;
  padding: 14px 10px;
  border-right: 1px solid #EEF0F4;
  background: #FFFFFF;
}

.gx-mc__subnav-item {
  display: block;
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: #4A4A6A;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  text-align: left;
  transition: color .2s, background .2s;
}

.gx-mc__subnav-item:hover {
  color: #C8443C;
  background: #FFF8F8;
}

.gx-mc__subnav-item--on {
  background: #FFF5F5;
  color: #C8443C;
  font-weight: 600;
  box-shadow: inset 3px 0 0 #C8443C;
}

.gx-mc__content {
  flex: 1;
  min-width: 0;
}

.gx-mc__summary {
  display: flex;
  align-items: center;
  gap: 8px 16px;
  flex-wrap: wrap;
  padding: 13px 20px;
  border-bottom: 1px solid #EEF0F4;
  background: #FAFBFC;
  color: #8A95A0;
  font-size: 12px;
  line-height: 20px;
}

.gx-mc__summary-item b {
  color: #1A1A2E;
  font-weight: 600;
}

.gx-mc__section {
  padding: 18px 22px;
  border-bottom: 1px solid #EEF0F4;
  scroll-margin-top: 60px;
}

.gx-mc__section:last-child {
  border-bottom: 0;
}

.gx-mc__section-title {
  margin: 0 0 12px;
  color: #1A1A2E;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.gx-mc__section-desc {
  margin: 0 0 12px;
  color: #8A95A0;
  font-size: 12px;
  line-height: 20px;
}

.gx-mc__form-row {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  padding: 6px 0;
  font-size: 13px;
  line-height: 30px;
}

.gx-mc__form-label {
  color: #4A4A6A;
  text-align: left;
}

.gx-mc__form-value {
  min-width: 0;
  color: #1A1A2E;
  line-height: 30px;
}

.gx-mc__form-value--inline {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.gx-mc__form-value--inline .el-button + .el-button {
  margin-left: 0;
}

.gx-mc__action-btn {
  min-width: 120px;
  height: 32px;
  padding: 0 16px;
  font-size: 13px;
}

.gx-mc__sync-text {
  color: #A67C1A;
  font-size: 12px;
}

.gx-mc__hint {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 10px 0 0;
  color: #8A95A0;
  font-size: 12px;
  line-height: 20px;
}

.gx-mc__hint i {
  margin-top: 2px;
  color: #D4A946;
}

.gx-mc__hint--warn {
  color: #D48806;
}

.gx-mc__alert {
  margin: 12px 0;
}

.gx-mc__muted {
  color: #8A95A0;
  font-size: 12px;
}

.gx-mc__default-mark {
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: #F6FFED;
  color: #52A65C;
  font-size: 11px;
  line-height: 18px;
}

.gx-mc__attribute-panel {
  margin-top: 14px;
  border: 1px solid #E0E0EC;
  border-radius: 8px;
  background: #FFFFFF;
  overflow: hidden;
}

.gx-mc__template-panel {
  margin-top: 14px;
  padding: 18px 20px;
  border: 1px solid #E0E0EC;
  border-radius: 8px;
  background: #FFFFFF;
}

.gx-mc__panel-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.gx-mc__panel-heading {
  min-width: 0;
}

.gx-mc__panel-heading .gx-mc__section-title {
  margin-bottom: 4px;
}

.gx-mc__panel-heading .gx-mc__section-desc {
  margin-bottom: 0;
}

.gx-mc__panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.gx-mc__panel-actions .el-button + .el-button {
  margin-left: 0;
}

.gx-mc__footer {
  position: sticky;
  bottom: 0;
  z-index: 14;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: auto;
  padding: 12px 64px 12px 16px;
  border: 1px solid #E0E0EC;
  border-radius: 8px;
  background: #FFFFFF;
  box-shadow: 0 -4px 14px rgba(26, 26, 46, .05);
}

.gx-mc__footer-left,
.gx-mc__footer-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.gx-mc__footer-label {
  color: #4A4A6A;
  font-size: 12px;
  white-space: nowrap;
}

.gx-mc__footer-template {
  width: 220px;
}

.gx-mc__footer .el-button + .el-button {
  margin-left: 0;
}

.gx-mc__danger-btn {
  color: #C8443C;
  font-size: 12px;
}

.gx-mc__danger .gx-mc__danger-btn:focus,
.gx-mc__danger .gx-mc__danger-btn:hover,
.gx-mc__danger-btn:focus,
.gx-mc__danger-btn:hover {
  color: #A62F28;
}

.gx-mc__state-card {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 14px;
  padding: 28px 24px;
  border: 1px solid #E0E0EC;
  border-radius: 10px;
  background: #FFFFFF;
  color: #8A95A0;
}

.gx-mc__state-card > i {
  color: #D4A946;
  font-size: 30px;
}

.gx-mc__state-card--error > i {
  color: #C8443C;
}

.gx-mc__state-body {
  flex: 1;
  min-width: 220px;
}

.gx-mc__state-title {
  color: #1A1A2E;
  font-size: 15px;
  font-weight: 600;
}

.gx-mc__state-text {
  margin-top: 4px;
  color: #8A95A0;
  font-size: 12px;
  line-height: 20px;
}

.gx-mc__state-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.gx-mc__option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.gx-mc__option-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gx-mc__option-tags {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.gx-mc__option-meta {
  color: #8A95A0;
  font-size: 12px;
}

.gx-mc__option-warn {
  color: #C8443C;
  font-size: 12px;
}

.template-name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.template-name-cell .el-input {
  flex: 1;
}

.default-template-mark {
  flex: 0 0 auto;
  color: #52A65C;
  font-size: 13px;
}

.sync-dialog-tip {
  margin-bottom: 14px;
  color: #8A95A0;
  font-size: 13px;
  line-height: 20px;
}

.gx-mc__sync-error {
  margin-bottom: 14px;
}

.sync-template-select {
  width: 100%;
}

@media (max-width: 1200px) {
  .gx-mc__subnav {
    flex-basis: 156px;
    width: 156px;
  }

  .gx-mc__form-row {
    grid-template-columns: 130px minmax(0, 1fr);
  }
}

@media (max-width: 900px) {
  .gx-mc__head {
    align-items: flex-start;
  }

  .gx-mc__head-actions {
    width: 100%;
    margin-left: 0;
  }

  .gx-mc__shop-select {
    flex: 1;
    width: auto;
    min-width: 180px;
  }

  .gx-mc__basic-panel {
    display: block;
  }

  .gx-mc__subnav {
    position: static;
    display: flex;
    width: 100%;
    flex-basis: auto;
    gap: 6px;
    padding: 8px 10px;
    border-right: 0;
    border-bottom: 1px solid #EEF0F4;
    overflow-x: auto;
  }

  .gx-mc__subnav-item {
    width: auto;
    flex: none;
    height: 32px;
    padding: 0 12px;
    border: 1px solid #E0E0EC;
    white-space: nowrap;
  }

  .gx-mc__subnav-item--on {
    border-color: #F4C9C5;
    box-shadow: none;
  }

  .gx-mc__form-row {
    grid-template-columns: 110px minmax(0, 1fr);
    gap: 10px;
  }

  .gx-mc__footer {
    padding-right: 48px;
  }

  .gx-mc__section {
    padding: 16px;
  }

  .gx-mc__summary {
    padding: 10px 16px;
  }

  .gx-mc__footer {
    align-items: flex-start;
  }

  .gx-mc__footer-right {
    width: 100%;
  }

  .gx-mc__footer-template {
    flex: 1;
    width: auto;
    min-width: 160px;
  }
}

@media (max-width: 680px) {
  .gx-mc__heading {
    width: 100%;
  }

  .gx-mc__title {
    font-size: 20px;
  }

  .gx-mc__shop {
    max-width: 100%;
  }

  .gx-mc__tab {
    padding: 0 14px;
  }

  .gx-mc__form-row {
    display: block;
  }

  .gx-mc__form-label {
    display: block;
    margin-bottom: 2px;
    text-align: left;
  }

  .gx-mc__form-value {
    line-height: 24px;
  }

  .gx-mc__form-value--inline {
    align-items: flex-start;
  }

  .gx-mc__panel-actions {
    width: 100%;
  }

  .gx-mc__panel-actions .el-button {
    flex: 1;
  }

  .gx-mc__footer {
    padding-right: 16px;
  }

  .gx-mc__footer-left,
  .gx-mc__footer-right {
    width: 100%;
  }

  .gx-mc__footer-right .el-button {
    flex: 1;
  }

  .gx-mc__footer-template {
    width: 100%;
    flex: 1 0 100%;
  }

  .gx-mc__state-card {
    align-items: flex-start;
  }
}
</style>

<style scoped>
.gx-mc__workspace {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-top: 14px;
}

.gx-mc__side-nav {
  position: sticky;
  top: 45px;
  flex: 0 0 180px;
  width: 180px;
  align-self: flex-start;
  padding: 10px;
  border: 1px solid #E0E0EC;
  border-radius: 8px;
  background: #FFFFFF;
}

.gx-mc__side-nav-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: #4A4A6A;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  text-align: left;
  transition: color .2s, background .2s;
}

.gx-mc__side-nav-item:hover {
  color: #C8443C;
  background: #FFF8F8;
}

.gx-mc__side-nav-item--on {
  background: #FFF5F5;
  color: #C8443C;
  font-weight: 600;
  box-shadow: inset 3px 0 0 #C8443C;
}

.gx-mc__workspace-content {
  flex: 1;
  min-width: 0;
}

.gx-mc__workspace-content .gx-mc__attribute-panel,
.gx-mc__workspace-content .gx-mc__template-panel {
  margin-top: 0;
}

.gx-mc__dialog-card {
  padding: 20px;
  border: 1px solid #E0E0EC;
  border-radius: 8px;
  background: #FFFFFF;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .03);
}

.gx-mc__dialog-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.gx-mc__dialog-title {
  margin: 0;
  color: #1A1A2E;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.gx-mc__dialog-stats {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.gx-mc__stat-badge {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  background: #F0F7FF;
  color: #2B7DE0;
  font-size: 12px;
  line-height: 24px;
  white-space: nowrap;
}

.gx-mc__dialog-card .gx-mc__alert {
  margin: 12px 0;
}

.gx-mc__dialog-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 14px;
}

.gx-mc__dialog-primary,
.gx-mc__dialog-secondary {
  min-width: 112px;
  height: 34px;
  padding: 0 16px;
  font-size: 13px;
}

.gx-mc__dialog-tip {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 12px 0 0;
  color: #8A95A0;
  font-size: 12px;
  line-height: 20px;
}

.gx-mc__dialog-tip i {
  margin-top: 2px;
  color: #2B7DE0;
}

.gx-mc__dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #EEF0F4;
}

.gx-mc__dialog-note {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #606266;
  font-size: 12px;
  line-height: 20px;
}

.gx-mc__dialog-note i {
  color: #2B7DE0;
}

.gx-mc__dialog-footer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.gx-mc__section-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding: 12px 64px 12px 16px;
  border: 1px solid #E0E0EC;
  border-radius: 8px;
  background: #FFFFFF;
}

.gx-mc__section-note {
  color: #8A95A0;
  font-size: 12px;
  line-height: 20px;
}

.gx-mc__section-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.gx-mc__section-actions .el-button + .el-button {
  margin-left: 0;
}

@media (max-width: 1200px) {
  .gx-mc__side-nav {
    flex-basis: 156px;
    width: 156px;
  }
}

@media (max-width: 900px) {
  .gx-mc__workspace {
    display: block;
  }

  .gx-mc__side-nav {
    position: static;
    display: flex;
    width: 100%;
    flex-basis: auto;
    gap: 6px;
    padding: 8px 10px;
    overflow-x: auto;
  }

  .gx-mc__side-nav-item {
    width: auto;
    flex: none;
    height: 32px;
    padding: 0 12px;
    border: 1px solid #E0E0EC;
    white-space: nowrap;
  }

  .gx-mc__side-nav-item--on {
    border-color: #F4C9C5;
    box-shadow: none;
  }

  .gx-mc__workspace-content {
    margin-top: 10px;
  }

  .gx-mc__dialog-footer,
  .gx-mc__section-footer {
    align-items: flex-start;
  }

  .gx-mc__section-footer {
    padding-right: 48px;
  }

  .gx-mc__dialog-footer-actions,
  .gx-mc__section-actions {
    width: 100%;
  }

  .gx-mc__footer-template {
    flex: 1;
    width: auto;
    min-width: 160px;
  }
}

@media (max-width: 680px) {
  .gx-mc__dialog-card {
    padding: 16px;
  }

  .gx-mc__section-footer {
    padding-right: 16px;
  }

  .gx-mc__dialog-head {
    display: block;
  }

  .gx-mc__dialog-stats {
    margin-top: 10px;
  }

  .gx-mc__dialog-actions {
    align-items: stretch;
  }

  .gx-mc__dialog-primary,
  .gx-mc__dialog-secondary {
    flex: 1;
    width: 100%;
  }

  .gx-mc__dialog-footer-actions .gx-mc__footer-template,
  .gx-mc__section-actions .gx-mc__footer-template {
    flex: 1 0 100%;
    width: 100%;
  }

  .gx-mc__dialog-footer-actions .el-button,
  .gx-mc__section-actions .el-button {
    flex: 1;
  }
}
</style>

<style scoped>
.gx-mc__template-footer {
  margin-top: 16px;
  padding-top: 14px;
  border: 0;
  border-top: 1px solid #EEF0F4;
  border-radius: 0;
}
</style>

<style scoped>
.gx-mc__basic-layout {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-top: 14px;
}

.gx-mc__basic-nav {
  position: sticky;
  top: 45px;
  flex: 0 0 180px;
  width: 180px;
  align-self: flex-start;
  padding: 10px;
  border: 1px solid #E0E0EC;
  border-radius: 8px;
  background: #FFFFFF;
}

.gx-mc__basic-nav-item {
  display: flex;
  align-items: center;
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: #4A4A6A;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  text-align: left;
}

.gx-mc__basic-nav-item--on {
  background: #FFF5F5;
  color: #C8443C;
  font-weight: 600;
  box-shadow: inset 3px 0 0 #C8443C;
}

.gx-mc__basic-content {
  flex: 1;
  min-width: 0;
}

.gx-mc__install-alert {
  margin-top: 14px;
}

.gx-mc__mode-options {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 28px;
  padding: 14px 0 4px;
}

.gx-mc__mode-options .el-radio {
  margin-right: 0;
  line-height: 24px;
}

.gx-mc__install-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 24px;
  padding-top: 14px;
  border-top: 1px solid #EEF0F4;
}

.gx-mc__install-footer-note {
  color: #8A95A0;
  font-size: 12px;
  line-height: 20px;
}

.gx-mc__install-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.gx-mc__install-footer-actions .el-button + .el-button {
  margin-left: 0;
}

.gx-mc__install-card {
  padding: 20px;
  border: 1px solid #E0E0EC;
  border-radius: 8px;
  background: #FFFFFF;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .03);
}

.gx-mc__install-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  padding-bottom: 14px;
  border-bottom: 1px solid #EEF0F4;
}

.gx-mc__install-title {
  margin: 0;
  color: #1A1A2E;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.gx-mc__install-desc {
  margin: 0;
  color: #8A95A0;
  font-size: 12px;
  line-height: 20px;
}

.gx-mc__install-body {
  padding-top: 18px;
}

.gx-mc__install-enable {
  margin-bottom: 16px;
}

.gx-mc__install-row {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  padding: 8px 0;
}

.gx-mc__install-label {
  color: #4A4A6A;
  font-size: 13px;
  text-align: left;
}

.gx-mc__install-value {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}

.gx-mc__install-select {
  width: 280px;
}

.gx-mc__install-tip {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 14px 0 0;
  color: #8A95A0;
  font-size: 12px;
  line-height: 20px;
}

.gx-mc__install-tip i {
  margin-top: 2px;
  color: #C8443C;
}

.gx-mc__attribute-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  flex-wrap: wrap;
  margin-top: 14px;
  padding: 12px 16px;
  border: 1px solid #E0E0EC;
  border-radius: 8px;
  background: #FFFFFF;
}

.gx-mc__attribute-stats {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.gx-mc__attribute-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.gx-mc__attribute-actions .el-button + .el-button {
  margin-left: 0;
}

.gx-mc__attribute-panel {
  margin-top: 12px;
}

@media (max-width: 900px) {
  .gx-mc__basic-layout {
    display: block;
  }

  .gx-mc__basic-nav {
    position: static;
    display: flex;
    width: 100%;
    flex-basis: auto;
    gap: 6px;
    padding: 8px 10px;
    overflow-x: auto;
  }

  .gx-mc__basic-nav-item {
    width: auto;
    flex: none;
    height: 32px;
    padding: 0 12px;
    border: 1px solid #E0E0EC;
    white-space: nowrap;
  }

  .gx-mc__basic-nav-item--on {
    border-color: #F4C9C5;
    box-shadow: none;
  }

  .gx-mc__basic-content {
    margin-top: 10px;
  }

  .gx-mc__basic-layout {
    display: block;
  }

  .gx-mc__basic-nav {
    position: static;
    display: flex;
    width: 100%;
    flex-basis: auto;
    gap: 6px;
    padding: 8px 10px;
    overflow-x: auto;
  }

  .gx-mc__basic-nav-item {
    width: auto;
    flex: none;
    height: 32px;
    padding: 0 12px;
    border: 1px solid #E0E0EC;
    white-space: nowrap;
  }

  .gx-mc__basic-nav-item--on {
    border-color: #F4C9C5;
    box-shadow: none;
  }

  .gx-mc__basic-content {
    margin-top: 10px;
  }

  .gx-mc__attribute-head {
    align-items: flex-start;
  }
}

@media (max-width: 680px) {
  .gx-mc__install-card {
    padding: 16px;
  }

  .gx-mc__mode-options {
    gap: 10px 18px;
  }

  .gx-mc__install-footer-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .gx-mc__install-footer-actions .gx-mc__footer-template {
    flex: 1 0 100%;
    width: 100%;
  }

  .gx-mc__install-footer-actions .el-button {
    flex: 1;
  }

  .gx-mc__attribute-actions {
    width: 100%;
  }

  .gx-mc__attribute-actions .el-button {
    flex: 1;
  }
}
</style>

<style scoped>
.gx-mc__basic-full {
  margin-top: 14px;
}

.gx-mc__attribute-tab {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-top: 14px;
}

.gx-mc__inner-nav {
  position: sticky;
  top: 45px;
  flex: 0 0 180px;
  width: 180px;
  align-self: flex-start;
  padding: 10px;
  border: 1px solid #E0E0EC;
  border-radius: 8px;
  background: #FFFFFF;
}

.gx-mc__inner-nav-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: #4A4A6A;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  text-align: left;
  transition: color .2s, background .2s;
}

.gx-mc__inner-nav-item:hover {
  color: #C8443C;
  background: #FFF8F8;
}

.gx-mc__inner-nav-item--on {
  background: #FFF5F5;
  color: #C8443C;
  font-weight: 600;
  box-shadow: inset 3px 0 0 #C8443C;
}

.gx-mc__inner-content {
  flex: 1;
  min-width: 0;
}

.gx-mc__inner-panel {
  min-width: 0;
}

.gx-mc__attribute-tab .gx-mc__attribute-panel {
  margin-top: 0;
}

@media (max-width: 900px) {
  .gx-mc__attribute-tab {
    display: block;
  }

  .gx-mc__inner-nav {
    position: static;
    display: flex;
    width: 100%;
    flex-basis: auto;
    gap: 6px;
    padding: 8px 10px;
    overflow-x: auto;
  }

  .gx-mc__inner-nav-item {
    width: auto;
    flex: none;
    height: 32px;
    padding: 0 12px;
    border: 1px solid #E0E0EC;
    white-space: nowrap;
  }

  .gx-mc__inner-nav-item--on {
    border-color: #F4C9C5;
    box-shadow: none;
  }

  .gx-mc__inner-content {
    margin-top: 10px;
  }
}
</style>

<style scoped>
.gx-mc__attribute-tab .gx-mc__template-panel {
  margin-top: 0;
}
</style>
